import React, {
  createContext,
  forwardRef,
  useContext,
  useId,
  useState,
} from 'react';
import styles from './Accordion.module.css';

/* ── Helpers ── */

const cx = (...classes: (string | false | null | undefined)[]) =>
  classes.filter(Boolean).join(' ');

/* ── Context ── */

interface AccordionContextValue {
  openItems: Set<string>;
  toggle: (id: string) => void;
  multiple: boolean;
}

const AccordionContext = createContext<AccordionContextValue | null>(null);

function useAccordion() {
  const ctx = useContext(AccordionContext);
  if (!ctx) throw new Error('Accordion.Item must be inside Accordion');
  return ctx;
}

/* ── Types ── */

export interface AccordionProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  multiple?: boolean;
  defaultOpen?: string | string[];
  children: React.ReactNode;
}

export interface AccordionItemProps extends React.ComponentPropsWithoutRef<'div'> {
  id?: string;
  /** Full header slot — rendered left of the chevron. May contain interactive elements. */
  header: React.ReactNode;
  disabled?: boolean;
  /** Called after the item is toggled; receives the new open state. */
  onToggle?: (open: boolean) => void;
  children: React.ReactNode;
}

/* ── Accordion (root) ── */

const AccordionRoot = forwardRef<HTMLDivElement, AccordionProps>(
  ({ multiple = false, defaultOpen, className, children, ...rest }, ref) => {
    const initialOpen = new Set(
      defaultOpen == null
        ? []
        : Array.isArray(defaultOpen)
        ? defaultOpen
        : [defaultOpen],
    );

    const [openItems, setOpenItems] = useState<Set<string>>(initialOpen);

    const toggle = (id: string) => {
      setOpenItems(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          if (!multiple) next.clear();
          next.add(id);
        }
        return next;
      });
    };

    return (
      <AccordionContext.Provider value={{ openItems, toggle, multiple }}>
        <div ref={ref} className={cx(styles.accordion, className)} {...rest}>
          {children}
        </div>
      </AccordionContext.Provider>
    );
  },
);

AccordionRoot.displayName = 'Accordion';

/* ── Accordion.Item ── */

const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ id: externalId, header, disabled, onToggle, className, children, ...rest }, ref) => {
    const generatedId = useId();
    const id = externalId ?? generatedId;
    const { openItems, toggle } = useAccordion();
    const isOpen = openItems.has(id);

    const panelId = `${id}-panel`;

    const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
      if (disabled) return;
      const target = e.target as HTMLElement;
      // don't toggle if a nested interactive element was clicked
      const interactiveNode = target.closest('button, a, input, select, textarea, label, [role="button"]');
      if (interactiveNode && interactiveNode !== e.currentTarget && e.currentTarget.contains(interactiveNode)) {
        return;
      }
      toggle(id);
      onToggle?.(!isOpen);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!disabled) {
          toggle(id);
          onToggle?.(!isOpen);
        }
      }
    };

    return (
      <div
        ref={ref}
        className={cx(styles.item, isOpen && styles.open, disabled && styles.disabled, className)}
        {...rest}
      >
        {/* Trigger row — div instead of button to allow nested interactive elements */}
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-expanded={isOpen}
          aria-controls={panelId}
          className={styles.trigger}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
        >
          <div className={styles.headerSlot}>{header}</div>
          <svg
            className={styles.chevron}
            aria-hidden="true"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M4 6l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div
          id={panelId}
          role="region"
          className={styles.panel}
        >
          <div className={styles.panelInner}>{children}</div>
        </div>
      </div>
    );
  },
);

AccordionItem.displayName = 'Accordion.Item';

/* ── Compound export ── */

export const Accordion = Object.assign(AccordionRoot, {
  Item: AccordionItem,
});
